import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

import Sidebar from 'components/Sidebar';
import AppBanner from 'components/Sidebar/AppBanner';
import FeaturedOffersList from 'components/Sidebar/FeaturedOffersList';
import SidebarGoogleAd from 'components/Sidebar/GoogleAd';

import useQueryClient from 'hooks/useQueryClient';

import { toSidebarFeaturedOffers } from 'lib/offer';
import { offerQueryKeys, searchQueryKeys } from 'lib/queryKeys';

import { getFeaturedOffersList } from 'services/offer';
import { getSuggestedSearchKeywordsList } from 'services/search';

import SearchResultsFilters from './Filters';

const SearchResultsSidebar = ({
  gaEventCategory,
  hasFilters = false,
  isLoadingResults = true,
  resultCounters = null,
  searchQuery = null,
  searchResultsType = null,
}) => {
  const queryClient = useQueryClient();
  const searchResultsFiltersRef = useRef(null);
  const midGoogleAdRef = useRef(null);
  const featuredOffersListRef = useRef(null);

  const {
    data: recommendedOffers,
    isLoading: isLoadingRecommendedOffers,
    isFetching: isFetchingRecommendedOffers,
  } = useQuery({
    queryKey: searchQueryKeys.recommendedLists(),
    queryFn: ({ signal }) =>
      getSuggestedSearchKeywordsList({ withOffers: true }, { signal }),
    select: (data) => data?.recommendedOffers ?? [],
    refetchOnMount: 'always',
    staleTime: Infinity,
  });

  const {
    data: featuredOffers,
    isLoading: isLoadingFeaturedOffers,
    isFetching: isFetchingFeaturedOffers,
  } = useQuery({
    queryKey: offerQueryKeys.listByFeatured(),
    queryFn: ({ signal }) => getFeaturedOffersList({ signal }),
    select: (data) => data ?? [],
    refetchOnMount: 'always',
    staleTime: Infinity,
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries(searchQueryKeys.trendsList());
    };
  }, []);

  const isLoading =
    isLoadingRecommendedOffers ||
    isLoadingFeaturedOffers ||
    isFetchingFeaturedOffers ||
    isFetchingRecommendedOffers;

  const sidebarFeaturedOffers = isLoading
    ? []
    : toSidebarFeaturedOffers(recommendedOffers, featuredOffers);

  return (
    <Sidebar
      className="hidden gap-y-4 xl:flex"
      position="left"
      refsToBeIgnoredBeforeFixing={[
        searchResultsFiltersRef,
        midGoogleAdRef,
        featuredOffersListRef,
      ]}
    >
      <SearchResultsFilters
        ref={searchResultsFiltersRef}
        gaEventAction="filter_by"
        gaEventCategory={gaEventCategory}
        hasFilters={hasFilters && resultCounters}
        isLoadingResults={isLoadingResults}
        resultCounters={resultCounters}
        searchQuery={searchQuery}
        searchResultsType={searchResultsType}
        title="Filtrar por"
      />
      <SidebarGoogleAd
        ref={midGoogleAdRef}
        adId="search_page_sidebar_1_300x250"
        adPath="/promobit.com.br_search_page/sidebar_1_300x250"
      />
      <FeaturedOffersList
        ref={featuredOffersListRef}
        featuredOffers={sidebarFeaturedOffers}
        isLoading={isLoading}
      />
      <AppBanner />
      <SidebarGoogleAd
        adId="search_page_sidebar_2_300x250"
        adPath="/promobit.com.br_search_page/sidebar_2_300x250"
      />
    </Sidebar>
  );
};

SearchResultsSidebar.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  hasFilters: PropTypes.bool,
  isLoadingResults: PropTypes.bool,
  resultCounters: PropTypes.shape({
    activeOffers: PropTypes.number.isRequired,
    finishedOffers: PropTypes.number.isRequired,
    coupons: PropTypes.number.isRequired,
    articles: PropTypes.number.isRequired,
    threads: PropTypes.number.isRequired,
    users: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }),
  searchQuery: PropTypes.string,
  searchResultsType: PropTypes.shape({
    NAME: PropTypes.string.isRequired,
    QUERY: PropTypes.string.isRequired,
    TYPE: PropTypes.string,
    BUTTON_TEXT: PropTypes.string,
    GA_EVENT_ACTION_PREFIX: PropTypes.string,
    GENERAL_RESULTS_LIMIT: PropTypes.number.isRequired,
    SORT_OPTIONS: PropTypes.arrayOf(
      PropTypes.shape({
        isDefaultOption: PropTypes.bool.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ),
  }),
};

export default SearchResultsSidebar;
